import React, { useState, useRef, useEffect } from 'react';
    import { motion, AnimatePresence } from 'framer-motion';
    import { useDebug } from '@/contexts/DebugContext';
    import { FiTerminal, FiX, FiTrash2, FiCopy, FiGlobe, FiCpu, FiDownload } from 'react-icons/fi';
    import toast from 'react-hot-toast';

    const LEVEL_STYLES = {
      log: 'text-slate-400',
      debug: 'text-purple-400',
      info: 'text-blue-400',
      warn: 'text-amber-400',
      error: 'text-rose-400 font-bold',
    };

    export function DebugConsole() {
      const { debugEnabled, logs, clearLogs } = useDebug();
      const [open, setOpen] = useState(false);
      const [filter, setFilter] = useState('all');
      const bottomRef = useRef(null);

      const filtered = filter === 'all' ? logs : logs.filter(l => l.level === filter || l.type === filter);

      useEffect(() => {
        if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, [logs, open]);

      const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard', { id: 'copy-log', style: { fontSize: '10px' } });
      };

      const copyAllLogs = () => {
        const fullText = filtered.map(log => `[${log.timestamp}] [${log.type.toUpperCase()}] [${log.level.toUpperCase()}] ${log.message}`).join('\n\n');
        copyToClipboard(fullText);
      };

      if (!debugEnabled) return null;

      return (
        <>
          <button 
            onClick={() => setOpen(o => !o)}
            className="fixed bottom-4 right-4 z-[9998] flex items-center gap-2 px-4 py-2 bg-slate-900 border border-slate-700 text-white rounded-xl shadow-2xl hover:border-primary-500 transition-all active:scale-95"
          >
            <FiTerminal className={logs.some(l => l.level === 'error') ? 'text-rose-500 animate-pulse' : 'text-primary-400'} />
            <span className="text-[10px] font-black uppercase tracking-widest">Debug Active</span>
          </button>

          <AnimatePresence>
            {open && (
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.95 }}
                className="fixed bottom-16 right-4 z-[9999] w-[min(900px,95vw)] h-[600px] bg-slate-950 border border-slate-800 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
              >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-slate-900 border-b border-slate-800 shrink-0">
                  <div className="flex gap-1 overflow-x-auto no-scrollbar">
                    {['all', 'network', 'error', 'warn', 'info'].map(f => (
                      <button 
                        key={f} 
                        onClick={() => setFilter(f)}
                        className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase transition-colors whitespace-nowrap ${filter === f ? 'bg-primary-500 text-white' : 'text-slate-500 hover:text-slate-300 bg-white/5'}`}
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={copyAllLogs} className="flex items-center gap-1 text-[10px] uppercase font-bold tracking-widest text-primary-400 hover:text-primary-300 transition-colors bg-primary-500/10 px-2 py-1 rounded">
                      <FiDownload size={12} /> Copy All
                    </button>
                    <button onClick={clearLogs} className="text-slate-500 hover:text-rose-400 transition-colors"><FiTrash2 size={14} /></button>
                    <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-white transition-colors"><FiX size={18} /></button>
                  </div>
                </div>

                {/* Log List */}
                <div className="flex-1 overflow-y-auto p-4 font-mono text-[11px] space-y-2 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]">
                  {filtered.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-slate-700 uppercase font-black tracking-widest opacity-20">Monitoring Activity...</div>
                  ) : (
                    filtered.map(log => {
                      const isNetwork = log.type === 'network';
                      const isError = log.level === 'error';
                      const isSuccess = log.message.includes('[200]') || log.message.includes('[201]') || log.message.includes('[204]');

                      return (
                        <div key={log.id} className={`group relative p-2.5 rounded-xl border transition-all ${isNetwork ? 'bg-blue-500/5 border-blue-500/10' : 'bg-white/5 border-transparent'} hover:border-slate-700`}>
                          <div className="flex items-center justify-between mb-1.5">
                            <div className="flex items-center gap-2">
                              {isNetwork ? <FiGlobe className="text-blue-400" size={10} /> : <FiCpu className="text-slate-500" size={10} />}
                              <span className={`${isNetwork ? 'text-blue-400' : LEVEL_STYLES[log.level]} uppercase font-black text-[9px] tracking-tighter`}>
                                {isNetwork ? 'Network' : log.level}
                              </span>
                              <span className="text-slate-600 text-[9px]">{new Date(log.timestamp).toLocaleTimeString()}</span>
                            </div>
                            <button 
                              onClick={() => copyToClipboard(`[${log.timestamp}] [${log.type.toUpperCase()}] [${log.level.toUpperCase()}] ${log.message}`)}
                              className="opacity-0 group-hover:opacity-100 text-primary-500 hover:text-primary-400 transition-opacity p-1 bg-slate-900 rounded-md"
                            >
                              <FiCopy size={12} />
                            </button>
                          </div>
                          <pre className={`whitespace-pre-wrap break-all leading-relaxed ${isNetwork ? (isSuccess ? 'text-emerald-400 font-bold' : isError ? 'text-rose-400 font-bold' : 'text-blue-300') : LEVEL_STYLES[log.level]}`}>
                            {log.message}
                          </pre>
                        </div>
                      );
                    })
                  )}
                  <div ref={bottomRef} />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      );
    }